import type { ReactNode } from 'react';
import type { FlightPlannerCopy } from './planner_copy.js';
import { legsValid } from './leg_state.js';
import type { PlannerTripType } from './planner_models.js';
import type { CtaRef } from './guided_fill.js';
import { CardFooter } from './card_footer.js';
import { InlineSearchCta, MultiReset } from './planner_ctas.js';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import type { PlannerBodyProps } from './planner_form_model.js';
import { TripTypeTabs } from './planner_fields.js';

/*
 * A moldura do planner por degrau de layout (faixa, colapsado, aberto com e
 * sem casca) e os CTAs inline dos modificadores do aberto. Saíram de
 * `planner_form.tsx` pelo teto de linhas dele.
 */

export function PlannerLayoutView({
  band,
  collapsed,
  naked,
  inlineSearch,
  showTabs,
  isMulti,
  activeTripType,
  readOnly,
  selectTripType,
  legsField,
  routeRow,
  whenField,
  whoRow,
  legsBuilder,
  multiFooterCta,
  note,
  search,
  ctaLabel,
  ctaRef,
  onCancel,
  copy,
}: {
  readonly band: boolean;
  readonly collapsed: boolean;
  /** O aberto sem casca (#2167) — ver a doc da prop em `PlannerFormProps`. O CTA
   *  inline já vem DENTRO de `routeRow`/`legsBuilder`; aqui só a moldura muda. */
  readonly naked: boolean;
  /** O aberto COM casca e SEM rodapé (#2207) — ver a doc da prop em
   *  `PlannerFormProps`. O CTA inline já vem dentro de `routeRow`/`legsBuilder`;
   *  aqui só a moldura muda. */
  readonly inlineSearch: boolean;
  readonly showTabs: boolean;
  readonly isMulti: boolean;
  readonly activeTripType: PlannerTripType;
  readonly readOnly: boolean;
  readonly selectTripType: (type: PlannerTripType) => void;
  readonly legsField: ReactNode;
  readonly routeRow: ReactNode;
  readonly whenField: ReactNode;
  /** O Quem ÚNICO, isolado acima do corpo nos dois modos (#2136 no multi, #2362
   *  no binário) — o valor é da viagem, não da perna nem da rota. `null` com
   *  `hideWho`; a faixa e o colapsado não o renderizam. */
  readonly whoRow: ReactNode;
  readonly legsBuilder: ReactNode;
  /** O CTA do multi na régua do RODAPÉ (`Button` lg, 36px) — a saída do card nas três
   *  abas mora na mesma saia (UX Review Pacote 2, M2), com o Redefinir do multi
   *  ao lado (#2362). O nó da linha do status (`multiCta`, cápsula sm) segue
   *  existindo só para a FAIXA da bancada. */
  readonly multiFooterCta: ReactNode;
  readonly note?: string;
  /** O Buscar do binário: o gate e o gesto de `PlannerGuide.searchCta`. */
  readonly search: { readonly disabled: boolean; readonly onClick: () => void };
  readonly ctaLabel: string;
  /** O Buscar do rodapé que o guia foca com o pedido completo (`useGuidedFill`). */
  readonly ctaRef: CtaRef;
  readonly onCancel?: () => void;
  readonly copy: FlightPlannerCopy;
}) {
  const tabs = showTabs ? (
    <TripTypeTabs
      active={activeTripType}
      disabled={readOnly}
      onSelect={selectTripType}
      copy={copy}
    />
  ) : null;
  // O QUEM isolado em cima nos DOIS modos (#2362, frames 445-*): é o sujeito da
  // viagem, não um campo da rota — antes ele abria a fileira binária.
  const body = (
    <>
      {whoRow}
      {isMulti ? legsBuilder : routeRow}
    </>
  );
  if (band) {
    return (
      <div className="fpl fpl-band">
        {tabs}
        {isMulti ? legsField : routeRow}
      </div>
    );
  }
  if (collapsed) return isMulti ? legsField : whenField;
  // NAKED (#2167): o mesmo corpo do card, sem a casca nem o rodapé — o CTA
  // inline já mora na fileira (binário) ou na linha de status (multi).
  if (naked) {
    return (
      <div className="fpl fpl-naked">
        {tabs}
        {body}
      </div>
    );
  }
  // CHAT sem saia (#2207): a casca de card FICA — é a bolha do turno —, o rodapé
  // SAI e o disparo já mora inline (lupa only-icon) na fileira ou na linha de
  // status. Sem `CardFooter`, o card mede o conteúdo e nada mais.
  if (inlineSearch) {
    return (
      <div className="cd fpl overflow-visible">
        <div className="cd-body">
          {tabs}
          {body}
        </div>
      </div>
    );
  }
  // A saída segura da EDIÇÃO (#2129): ghost imediatamente à esquerda do CTA, como
  // o Cancelar da topbar do protótipo — descarta o rascunho, nunca compete com o
  // Buscar. Mesma receita do Redefinir (`Button` ghost do DS; `bg-transparent`
  // desfaz o fundo do preflight escopado, que o ghost não cobre).
  const cancel =
    !readOnly && onCancel ? (
      <Button
        type="button"
        variant="ghost"
        size="lg"
        className="genui2 bg-transparent"
        onClick={onCancel}
      >
        {copy.cancelEdit}
      </Button>
    ) : null;
  // Sem grade inline em NENHUMA aba (#2136): o calendário mora no popover do
  // QUANDO (binário) e no popover de cada linha (multi) — o card mede o mesmo
  // nas três abas, e o gesto de abrir a data esconde a latência do heat.
  return (
    <div className="cd fpl overflow-visible">
      <div className="cd-body">
        {tabs}
        {body}
      </div>
      <CardFooter note={note}>
        {/* No binário a saia Redefinir/Confirmar mora no popover do intervalo
            (#2136) e aqui fica só a saída — Buscar. No multi a saia é do
            componente (#2362): Redefinir (ghost) à esquerda do Buscar, dentro de
            `multiFooterCta`. */}
        {!readOnly && isMulti && (
          <div className="fui-actions">
            {cancel}
            {multiFooterCta}
          </div>
        )}
        {!readOnly && !isMulti && (
          <div className="fui-actions">
            {cancel}
            <Button
              ref={ctaRef}
              type="button"
              size="lg"
              className="genui2 rounded-full"
              {...search}
            >
              {ctaLabel}
            </Button>
          </div>
        )}
      </CardFooter>
    </div>
  );
}

/** Os CTAs inline dos DOIS modificadores do layout aberto — fora do `PlannerBody`
 *  pelo teto de complexidade dele. `row` fecha a fileira binária (na coluna
 *  `.fpl-field-cta`, como o CTA da faixa) e `multiCta` mora na linha de status da
 *  lista. Os dois booleanos devolvidos são os modificadores EFETIVOS (só o layout
 *  aberto; band/collapsed ignoram as props) e são MUTUAMENTE EXCLUSIVOS — é o que
 *  o `PlannerLayoutView` consome pra escolher a moldura. */
export function inlineCtas(props: PlannerBodyProps): {
  naked: boolean;
  inlineSearch: boolean;
  row: ReactNode;
  multiCta: ReactNode;
} {
  const { band, collapsed, readOnly, state, binary, multi, ctaLabel, submitDates } = props;
  const { onReset, copy } = props;
  const openLayout = !band && !collapsed;
  const naked = Boolean(props.naked) && openLayout;
  // `naked` vence: ele já não tem rodapé, e a tela determinística mostra o rótulo.
  const inlineSearch = !naked && Boolean(props.inlineSearch) && openLayout;
  if ((!naked && !inlineSearch) || readOnly) {
    return { naked, inlineSearch, row: null, multiCta: null };
  }
  return {
    naked,
    inlineSearch,
    // A classe extra no WRAPPER (não no botão) é o que importa pro CSS: é a
    // `div.fpl-field-cta`, não o `<button>`, que é o item de flex da
    // `.fpl-filter` — só ali um `margin-left: auto` desloca alguma coisa
    // (#2207, ver `.fpl-field-cta-icon` no CSS). Só no only-icon: o "Buscar"
    // com rótulo do naked (`iconOnly` falso) segue como sempre.
    row: (
      <div className={`fpl-field fpl-field-cta${inlineSearch ? ' fpl-field-cta-icon' : ''}`}>
        {/* No celular o Buscar da PÁGINA fecha a coluna em largura cheia e 44px
            (#2647) — só o naked: no card a lupa de borda a borda não é botão, é
            faixa. Utilitárias, não folha: a legada perde pro `h-9` do átomo. */}
        <InlineSearchCta
          ref={props.guide.ctaRef}
          {...props.guide.searchCta(false, binary.ready, submitDates)}
          label={ctaLabel}
          iconOnly={inlineSearch}
          {...(naked ? { className: 'max-sm:h-11 max-sm:w-full' } : {})}
        />
      </div>
    ),
    // A saia do multi na linha do status (#2362): Redefinir + Buscar agrupados
    // num nó só — a `.fpl-legs-foot` é `space-between` entre a frase e a saída,
    // e dois filhos soltos iriam parar um em cada ponta.
    multiCta: (
      <div className="fpl-legs-actions">
        <MultiReset
          onReset={onReset}
          copy={copy}
          large={false}
          {...(naked ? { className: 'max-sm:h-11' } : {})}
        />
        <InlineSearchCta
          ref={props.guide.ctaRef}
          {...props.guide.searchCta(true, legsValid(state.legs), multi.submitMulti)}
          label={ctaLabel}
          iconOnly={inlineSearch}
          foot
          {...(naked ? { className: 'max-sm:h-11 max-sm:flex-1' } : {})}
        />
      </div>
    ),
  };
}
